import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { useIntl } from 'react-intl';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import { Button, ImageCardButton } from '@whitelabel/component-library';
import { ReactComponent as IconDollar } from '@whitelabel/media/icons/small/dollar.svg';
import { ReactComponent as IconDocument } from '@whitelabel/media/icons/small/document.svg';
import { ReactComponent as IconEdit } from '@whitelabel/media/icons/small/edit.svg';
import commonMessage from '@whitelabel/helpers/messages';
import { ICustomer, IUser, IHelpPagePartner, IError } from '../../helpers/types';
import { SKYSCANNER_PARTNER_ID } from '../../helpers/constants';
import { getPartners as getPartnersAction } from '../../actions';
import {
  StyledBGContainer,
  StyledTitle,
  StyledBottomBox,
  StyledLoginBlock,
  StyledPartnersBlock,
  StyledPartnersBox,
  StyledWelcomeText,
  StyledActionButton,
  StyledQuickActionsBox,
  StyledAlert,
} from './styledHelpCenterHero';
import messages from './messages';

interface IHelpCenterHeroProps {
  customer: ICustomer;
  hideLoginOnDesktop?: boolean;
  hidePartnersBlock?: boolean;
  partners?: IHelpPagePartner[];
  insPartners?: IHelpPagePartner[];
  loadingINSPartners?: boolean;
  getPartners(customerID: string): void;
  error?: IError;
  user?: IUser;
  noMarginBottom?: boolean;
}

const HelpCenterHero = ({
  hideLoginOnDesktop,
  hidePartnersBlock,
  noMarginBottom,
  customer,
  partners,
  insPartners,
  loadingINSPartners,
  getPartners,
  error,
  user,
}: IHelpCenterHeroProps): JSX.Element => {
  const theme = useTheme();
  const { formatMessage, locale, bidi } = useIntl();
  const location = useLocation();
  const history = useHistory();
  const [dummyBoxes, setDummyBoxes] = useState<number[]>();
  const isTablet = useMediaQuery({ minWidth: theme.gridBreakpoints.md });
  const isDesktop = useMediaQuery({ minWidth: theme.gridBreakpoints.lg });

  const localPartners = partners || insPartners;
  const hideBottomBoxOnDesktop = hideLoginOnDesktop && !user && !localPartners;

  useEffect(() => {
    function updateDummyBoxes(totalBoxesInARow: number) {
      const reminder = localPartners!.length % totalBoxesInARow;
      if (reminder) {
        setDummyBoxes(Array(totalBoxesInARow - reminder).fill(1));
      } else {
        setDummyBoxes(undefined);
      }
    }
    if (localPartners) {
      if (isDesktop) {
        updateDummyBoxes(4);
      } else if (isTablet) {
        updateDummyBoxes(3);
      } else {
        setDummyBoxes(undefined);
      }
    }
  }, [isTablet, isDesktop, localPartners]);

  useEffect(() => {
    if (user && customer && !hidePartnersBlock && !partners) {
      getPartners(customer.id);
    }
  }, [user, getPartners, customer, partners, hidePartnersBlock]);

  const renderLoginBlock = () => (
    <StyledLoginBlock>
      <div>
        <h2 className="h4">{formatMessage(messages.loginQuestion)}</h2>
        {formatMessage(messages.loginText)}
      </div>
      <Button
        buttonType="secondary"
        onClick={() => {
          history.push(`/${locale}/login`, { referrer: { ...location } });
        }}
      >
        {formatMessage(messages.loginButtonText)}
      </Button>
    </StyledLoginBlock>
  );

  function renderPartnersBlock() {
    return (
      <StyledPartnersBlock>
        <Container>
          <StyledWelcomeText $hasPartners={!!localPartners}>{formatMessage(messages.welcomeText)}</StyledWelcomeText>
          {error && <StyledAlert color="danger">{formatMessage(commonMessage.error)}</StyledAlert>}
          {localPartners && (
            <StyledPartnersBox>
              {localPartners.map(({ id, logo: logoRaw, title, slug }) => {
                let logo: undefined | string = logoRaw;
                if (id === SKYSCANNER_PARTNER_ID) {
                  // business requirement: don't show skyscanner logo
                  logo = undefined;
                }
                return (
                  <ImageCardButton
                    key={id}
                    image={logo}
                    title={formatMessage(messages.protectionText, { partnerName: title })}
                    link={`/help/partners/${slug}`}
                  />
                );
              })}
              {dummyBoxes && dummyBoxes.map((element, index) => <div key={element + index}></div>)}
            </StyledPartnersBox>
          )}
        </Container>
      </StyledPartnersBlock>
    );
  }

  const renderQuickActions = () => (
    <StyledQuickActionsBox>
      <h3 className="h5">{formatMessage(messages.quickActionsText)}</h3>
      <ul>
        <li>
          <StyledActionButton icon={IconDollar} to={`/${locale}/account/claims/fnol`}>
            {formatMessage(messages.makeAClaimText)}
          </StyledActionButton>
        </li>
        <li>
          <StyledActionButton icon={IconDocument} to={`/${locale}/account`}>
            {formatMessage(messages.viewProtectionText)}
          </StyledActionButton>
        </li>
        <li>
          <StyledActionButton icon={IconEdit} to={`/${locale}/profile`}>
            {formatMessage(messages.updateProfileText)}
          </StyledActionButton>
        </li>
      </ul>
    </StyledQuickActionsBox>
  );

  if (localPartners?.length === 1) {
    const redirectTo = `/${locale}/help/partners/${localPartners[0].slug}`;
    if (location.pathname !== redirectTo) {
      return <Redirect to={redirectTo} />;
    }
  }

  return (
    <>
      <StyledBGContainer $rtl={bidi} $hideBottomBoxOnDesktop={hideBottomBoxOnDesktop}>
        <Container>
          <StyledTitle>
            <span>{formatMessage(messages.title)}</span>
          </StyledTitle>
        </Container>
      </StyledBGContainer>
      {!loadingINSPartners && (
        <>
          {((user && customer) || localPartners) && !hidePartnersBlock && renderPartnersBlock()}
          <StyledBottomBox $hideOnDesktop={hideBottomBoxOnDesktop} $noMarginBottom={noMarginBottom}>
            {user || localPartners ? renderQuickActions() : renderLoginBlock()}
          </StyledBottomBox>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state: any) => {
  const { locale } = state.intl;
  const { data, error } = state.partners;

  return {
    customer: state.customer.data,
    partners: data[locale],
    error,
    user: state.user.data,
  };
};

const mapDispatchToProps = {
  getPartners: getPartnersAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(HelpCenterHero);
